import React, { useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'
import { useGallery } from '../contexts/GalleryContext'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import OptimizedImage from './OptimizedImage'
import { getImageUrl } from '../utils/imageUtils'

const Gallery = () => {
  const { t } = useTranslation()
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  const { 
    getActiveGalleryItems, 
    getGalleryItemsByCategory, 
    getCategoryMapping,
    loading 
  } = useGallery()

  const [activeFilter, setActiveFilter] = useState('all')
  const [selectedImage, setSelectedImage] = useState(null)
  const [visibleCount, setVisibleCount] = useState(6) // Show 6 images initially
  const IMAGES_PER_LOAD = 6 // Load 6 more images each time

  const filters = [
    { id: 'all', label: t('gallery.all') },
    { id: 'events', label: t('gallery.events') },
    { id: 'campus', label: t('gallery.campusLife') },
    { id: 'celebrations', label: t('gallery.celebrations') },
    { id: 'sports', label: t('gallery.sportsActivities') }
  ]

  // Get dynamic gallery images from context
  const galleryImages = getActiveGalleryItems().map(item => ({
    id: item._id, // Use MongoDB _id field
    src: getImageUrl(item.image), // Use utility function to get full URL
    alt: item.alt || item.title,
    category: item.category, // Keep original category name
    title: item.title,
    description: item.description,
    featured: item.featured
  }))

  const filteredImages = activeFilter === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => {
        const categoryMapping = getCategoryMapping()
        return img.category === categoryMapping[activeFilter]
      })

  // Get visible images based on pagination
  const visibleImages = filteredImages.slice(0, visibleCount)
  const hasMore = filteredImages.length > visibleCount

  // Reset visible count when filter changes
  React.useEffect(() => {
    setVisibleCount(6) // Reset to initial count when filter changes
  }, [activeFilter])

  const loadMoreImages = () => {
    setVisibleCount(prevCount => prevCount + IMAGES_PER_LOAD)
  }

  const openLightbox = (image) => {
    setSelectedImage(image)
    document.body.style.overflow = 'hidden'
  }

  const closeLightbox = () => {
    setSelectedImage(null)
    document.body.style.overflow = 'unset'
  }

  const navigateImage = (direction) => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id)
    let newIndex
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : filteredImages.length - 1
    } else {
      newIndex = currentIndex < filteredImages.length - 1 ? currentIndex + 1 : 0
    }
    
    setSelectedImage(filteredImages[newIndex])
  }

  return (
    <section id="gallery" className="py-16 lg:py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-1000 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-primary-purple font-poppins mb-4">
              {t('gallery.title')}
            </h2>
            <div className="w-24 h-1 bg-primary-yellow mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-4">
              {t('gallery.subtitle')}
            </p>
            <div className="flex items-center justify-center space-x-4 text-primary-purple bg-primary-purple/5 py-3 px-6 rounded-full max-w-md mx-auto">
              <span className="font-semibold text-sm">{t('gallery.clickToView')}</span>
            </div>
          </div>

          {/* Featured Campus Showcase - Only show images */}
          <div className="mb-16">
            {galleryImages.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {/* Large featured image */}
                <div className="md:col-span-2 lg:col-span-2">
                  <div 
                    className="relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer h-64 lg:h-80 group"
                    onClick={() => openLightbox(galleryImages[0])}
                  >
                    <OptimizedImage
                      src={galleryImages[0]?.src || '/campus/campas1.webp'}
                      alt={galleryImages[0]?.alt || t('gallery.campusMainView')}
                      className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                      loading="eager"
                      placeholder="blur"
                    />
                    <div className="w-full h-full bg-gray-100 hidden items-center justify-center fallback-content">
                      <div className="text-center text-gray-600">
                        <div className="text-6xl mb-4">🏫</div>
                        <div className="text-2xl font-bold">{galleryImages[0]?.title || t('gallery.campusMainView')}</div>
                      </div>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-6 left-6 right-6">
                        <h4 className="text-white text-xl font-bold mb-2">{galleryImages[0]?.title || t('gallery.campusMainView')}</h4>
                        <p className="text-white/90">{galleryImages[0]?.description || t('gallery.campusMainDescription')}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Two smaller images */}
                <div className="space-y-6">
                  {galleryImages[1] && (
                    <div 
                      className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 cursor-pointer h-28 lg:h-36 group"
                      onClick={() => openLightbox(galleryImages[1])}
                    >
                      <OptimizedImage
                        src={galleryImages[1].src}
                        alt={galleryImages[1].alt}
                        className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        placeholder="blur"
                      />
                      <div className="w-full h-full bg-gray-100 hidden items-center justify-center fallback-content">
                        <div className="text-center">
                          <div className="text-3xl mb-2">🏢</div>
                          <div className="font-bold">{galleryImages[1].title}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {galleryImages[2] && (
                    <div 
                      className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 cursor-pointer h-28 lg:h-36 group"
                      onClick={() => openLightbox(galleryImages[2])}
                    >
                      <OptimizedImage
                        src={galleryImages[2].src}
                        alt={galleryImages[2].alt}
                        className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        placeholder="blur"
                      />
                      <div className="w-full h-full bg-gray-100 hidden items-center justify-center fallback-content">
                        <div className="text-center">
                          <div className="text-3xl mb-2">🎓</div>
                          <div className="font-bold">{galleryImages[2].title}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  activeFilter === filter.id
                    ? 'bg-primary-purple text-white shadow-lg scale-105'
                    : 'bg-white text-primary-purple border-2 border-primary-purple hover:bg-primary-purple hover:text-white'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-purple"></div>
                <span className="text-primary-purple font-medium">{t('gallery.loadingGallery')}</span>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredImages.length === 0 && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">📸</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('gallery.noImagesFound')}</h3>
              <p className="text-gray-600 mb-6">
                {activeFilter === 'all' 
                  ? t('gallery.noImagesUploaded')
                  : t('gallery.noImagesInCategory', { category: filters.find(f => f.id === activeFilter)?.label })
                }
              </p>
              <button 
                onClick={() => setActiveFilter('all')}
                className="bg-primary-purple text-white px-6 py-3 rounded-lg hover:bg-primary-magenta transition-colors"
              >
                {t('gallery.viewAllImages')}
              </button>
            </div>
          )}

          {/* Gallery Grid - Enhanced for better image viewing */}
          {!loading && visibleImages.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
              {visibleImages.map((image, index) => (
              <div
                key={image.id}
                className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer aspect-[4/3] bg-gray-100"
                onClick={() => openLightbox(image)}
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              >
                {/* Optimized images with lazy loading */}
                <OptimizedImage
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                  placeholder="blur"
                />
                {/* Enhanced fallback placeholder */}
                <div className="w-full h-full bg-gray-100 hidden items-center justify-center border-2 border-dashed border-primary-purple/30 fallback-content">
                  <div className="text-center text-gray-500 p-4">
                    <div className="text-5xl mb-3">🏫</div>
                    <div className="font-semibold text-lg text-primary-purple">{image.title}</div>
                    <div className="text-sm capitalize mt-1 px-2 py-1 bg-primary-purple/10 rounded">{image.category}</div>
                    <div className="text-xs mt-2 text-gray-400">Click to view larger</div>
                  </div>
                </div>
                
                {/* Enhanced overlay with zoom indicator */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute top-4 right-4">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-2">
                      <div className="w-6 h-6 text-white">🔍</div>
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-semibold text-lg mb-1">
                      {image.title}
                    </h3>
                    <span className="text-white/90 text-sm capitalize bg-white/20 px-2 py-1 rounded">
                      {image.category}
                    </span>
                  </div>
                </div>

                {/* Hover Effect */}
                <div className="absolute inset-0 bg-primary-purple/10 scale-110 opacity-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-500"></div>
              </div>
            ))}
            </div>
          )}

          {/* Load More Button */}
          {!loading && hasMore && (
            <div className="text-center mt-12">
              <button 
                onClick={loadMoreImages}
                className="bg-primary-purple text-white px-8 py-4 rounded-xl font-semibold hover:bg-primary-magenta hover:scale-105 transition-all duration-300 shadow-lg"
              >
                {t('gallery.loadMoreImages')} ({filteredImages.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              aria-label={t('gallery.closeImage')}
            >
              <X size={32} />
            </button>

            {/* Navigation Buttons */}
            <button
              onClick={() => navigateImage('prev')}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
              aria-label={t('gallery.previousImage')}
            >
              <ChevronLeft size={32} />
            </button>
            <button
              onClick={() => navigateImage('next')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
              aria-label={t('gallery.nextImage')}
            >
              <ChevronRight size={32} />
            </button>

            {/* Image */}
            <div className="bg-white rounded-lg overflow-hidden">
              <OptimizedImage
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="w-full h-auto max-h-[70vh]"
                loading="eager"
                placeholder="blur"
              />
              {/* Fallback placeholder for lightbox */}
              <div className="aspect-video bg-gray-100 hidden items-center justify-center min-h-96 fallback-content">
                <div className="text-center text-gray-600">
                  <div className="text-6xl mb-4">📸</div>
                  <div className="text-2xl font-medium">{selectedImage.title}</div>
                  <div className="text-lg capitalize mt-2">{selectedImage.category}</div>
                </div>
              </div>
              
              {/* Image Info */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-primary-purple mb-2">
                  {selectedImage.title}
                </h3>
                <span className="inline-block bg-primary-purple/10 text-primary-purple px-3 py-1 rounded-full text-sm capitalize">
                  {selectedImage.category}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default Gallery